import { useState } from 'react';
import { Fab } from '@mui/material';
import axios, { AxiosError } from 'axios';
import classNames from 'classnames';
import _ from 'lodash';

import { Icon } from '../shared/Icon';
import ModalV2, {
    Button as ModalButton,
    ButtonKind,
    Footer,
    Status,
    StatusType,
} from '../shared/ModalV2';
import { UserType } from './UserType';
import { Checkbox, Loading } from '../shared';

import { getPackDisplayName } from '../utils/utils';

import { Pack } from '../types/packTypes';

import styles from './AddProductsButton.module.scss';

type AddProductsProps = {
    packsToAdd: Pack[];
    onSuccess: () => void;
    user: UserType;
};
export function AddProductsButton(props: AddProductsProps) {
    const { packsToAdd, user } = props;
    const [isModalShown, setIsModalShown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [hasAddedSuccessfully, setHasAddedSuccessfully] = useState(false);

    async function addPacks() {
        setIsLoading(true);
        const productIds = packsToAdd.map(
            (pack) => pack.priceLevels[0].productId,
        );

        const url =
            user.type === 'accountId'
                ? `/api/accounts/v2/account/${user.accountId}/addproducts`
                : `/api/accounts/v2/profile/${user.profileId}/addproducts`;

        try {
            await axios.post(url, {
                productIds,
            });
            setHasAddedSuccessfully(true);
            setErrorMessage('');
            props.onSuccess();
        } catch (error) {
            setErrorMessage(
                'Please try again or contact system administrator.',
            );
            if (error instanceof AxiosError) {
                const status: number | undefined = error.response?.status;
                if (status === 403) {
                    setErrorMessage(
                        `You've been logged out, please refresh page to login again.`,
                    );
                } else if (status === 401) {
                    setErrorMessage(
                        `You don't have permissions to add products. Please contact system administrator.`,
                    );
                }
            }
            setHasAddedSuccessfully(false);
        }
        setIsLoading(false);
    }

    const resetState = () => {
        setIsModalShown(false);
        setIsLoading(false);
        setErrorMessage('');
        setHasAddedSuccessfully(false);
    };

    const renderModalContent = () => {
        if (isLoading) {
            return <Loading />;
        }
        if (errorMessage !== '') {
            return (
                <>
                    <Status
                        type={StatusType.Error}
                        header="Oops, something went wrong"
                        body={errorMessage}
                    />
                    <Footer>
                        <ModalButton
                            kind={ButtonKind.SECONDARY}
                            onClick={resetState}
                        >
                            Close window
                        </ModalButton>
                    </Footer>
                </>
            );
        }

        if (hasAddedSuccessfully) {
            return (
                <>
                    <Status
                        type={StatusType.Info}
                        header={`Products successfully added to ${user.type === 'accountId' ? 'account' : 'profile'}`}
                        body={`Please close this window to go back to ${user.type === 'accountId' ? 'account' : 'profile'}'s page`}
                    />
                    <Footer>
                        <ModalButton
                            kind={ButtonKind.SECONDARY}
                            onClick={resetState}
                        >
                            Close
                        </ModalButton>
                    </Footer>
                </>
            );
        }

        const modalHeader =
            packsToAdd.length === 1
                ? 'Add 1 product'
                : `Add ${packsToAdd.length} products`;

        return (
            <>
                <h1>{modalHeader}</h1>
                <div
                    className={classNames(
                        styles.greyText,
                        styles.marginBottom20,
                    )}
                >
                    To {user.type === 'accountId' ? 'Account' : 'Profile'} ID:{' '}
                    <span className={styles.notBoldText}>
                        {user.type === 'accountId'
                            ? user.accountId
                            : user.profileId}
                    </span>
                </div>
                <div className={styles.modalContent}>
                    <div>Selected products ({packsToAdd.length})</div>
                    <div className={styles.packsList}>
                        {packsToAdd.map((pack, i) => (
                            <div className={styles.pack} key={'pack_' + i}>
                                <Checkbox
                                    key={pack.packId}
                                    disabled={true}
                                    label={`${getPackDisplayName(pack)} (${
                                        pack.worth
                                    })`}
                                    highlight={false}
                                    isSearching={false}
                                    checked={true}
                                    pack={pack}
                                    onToggle={_.noop}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <Footer>
                    <p
                        className={classNames(
                            styles.paragraph,
                            styles.greyText,
                        )}
                    >
                        Please note, this process can't be reversed confirm the
                        selected Profile ID and packs before continuing.
                    </p>
                    <div>
                        <ModalButton
                            kind={ButtonKind.SECONDARY}
                            onClick={() => setIsModalShown(false)}
                        >
                            Cancel
                        </ModalButton>
                        <ModalButton
                            kind={ButtonKind.PRIMARY}
                            onClick={addPacks}
                            disabled={!packsToAdd.length || isLoading}
                        >
                            Add products
                        </ModalButton>
                    </div>
                </Footer>
            </>
        );
    };

    return (
        <>
            <Fab
                variant="extended"
                onClick={() => setIsModalShown(true)}
                sx={{ position: 'fixed', right: 10, bottom: 10 }}
                disabled={packsToAdd.length === 0}
                color="primary"
            >
                <Icon name="add" style={{ fontSize: 20, marginRight: 4 }} />
                {packsToAdd.length === 0
                    ? 'No Packs Selected'
                    : `Add ${packsToAdd.length} product(s)`}
            </Fab>
            {isModalShown && <ModalV2>{renderModalContent()}</ModalV2>}
        </>
    );
}
